import { Section } from './Section';
import * as THREE from 'three';
import Time from '../Core/Time';
import Resources from '../Utils/Resources';
import gradientVertexShader from '../shaders/gradient2/vertex.glsl';
import gradientFragmentShader from '../shaders/gradient2/fragment.glsl';
import { Input } from '../Core/Input';
import { Sizes } from '../Core/Sizes';
import { Camera } from '../Core/Camera';
import vertexShader from '../shaders/subtract/vertex.glsl';
import fragmentShader from '../shaders/subtract/fragment.glsl';
import gsap from "gsap";

/**
 * Fourth section implementation
 * Displays a particle system that responds to scroll progress
 */
export class SectionTheorists extends Section {
  /**
   * Initialize section elements
   * Override base method
   */
  initialize() {
    // Call parent initialize to create background
    super.initialize();

    this.isInit = false;

    this.gradient = {
      colorTopLeft: new THREE.Color(0x1e81f0),
      colorBottomLeft: new THREE.Color(0x4d6a8c),
      colorTopRight: new THREE.Color(0x274160),
      colorBottomRight: new THREE.Color(0x6495c6)
    }

    this.camera = Camera.getInstance();
    this.sceneCamera = this.camera.instance;
    this.input = Input.getInstance();

    this.shapeColor = new THREE.Color(0x1876ea);
    this.shapeHighlightColor = this.shapeColor.clone().offsetHSL(0, 0, 0.25);
    this.colorLerp = 0.05;

    this.targetColors = [];
    this.currentColors = [];

    // Add opacity control
    this.defaultOpacity = 0.2;
    this.highlightOpacity = 1.0;
    this.opacityLerp = 0.05;
    this.targetOpacities = [];
    this.currentOpacities = [];

    this.noiseOpacity = 0.8;
    this.blendOpacity = 0.6;

    this.positionOffset = 0.01;

    this.resources = Resources.getInstance();
    this.sizes = Sizes.getInstance();

    // Get time instance for animation
    this.time = Time.getInstance();
    // Set up animation
    this.setupAnimation();

    this.bindAll();

    this.setupHTML();

    this.initializeMouseTracking();

    this.createGradientBackground();

    this.resources.on('group', (groupName) => {
      if(groupName === 'subtract'){
        this.createShape();
        this.createShapePattern();
        this.isInit = true;
      }
    });
  }

  bindAll(){
    this.onInputUpdate = this.onInputUpdate.bind(this);
  }

  createGradientBackground() {

    this.backgroundMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uColorTopLeft: { value: this.gradient.colorTopLeft },
        uColorBottomLeft: { value: this.gradient.colorBottomLeft },
        uColorTopRight: { value: this.gradient.colorTopRight },
        uColorBottomRight: { value: this.gradient.colorBottomRight },
        uNoiseOpacity: { value: this.noiseOpacity },
        uBlendOpacity: { value: this.blendOpacity },
        uResolution: { value: new THREE.Vector2(this.sectionData.worldData.width * 100, this.sectionData.worldData.height * 100) },
        uGrainSize: { value: 4.0 }
      },
      vertexShader: gradientVertexShader,
      fragmentShader: gradientFragmentShader,
      transparent: false,
      side: THREE.FrontSide
    });

    // Create mesh
    this.backgroundPlane.material = this.backgroundMaterial;

    // Scale the mesh to match section dimensions
    this.backgroundPlane.scale.set(
      this.sectionData.worldData.width,
      this.sectionData.worldData.height,
      1
    );
  }

  setupHTML(){
    this.html = {
      speakersList: document.querySelector('.gt-speakers-list'),
      speakersItems: document.querySelectorAll('.gt-speakers-list .gt-speaker-row')
    };

    // Track animation start times
    let lastAnimationStartTime = 0;
    const MIN_DELAY = 0.2; // Minimum delay between animations in seconds

    this.html.speakersItems.forEach(item => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none none",
          onEnter: () => {
            const now = performance.now() / 1000; // Convert to seconds
            const timeSinceLastAnimation = now - lastAnimationStartTime;
            const delay = Math.max(0, MIN_DELAY - timeSinceLastAnimation);

            tl.to(item, {
              duration: 0.5,
              delay: delay,
              ease: 'power2.inOut',
              onStart: () => {
                item.classList.add('is-appeared');
                lastAnimationStartTime = performance.now() / 1000;
              }
            });
          }
        }
      });
    });
  }

  initializeMouseTracking() {
    // Initialize mouse position tracking
    this.mouse = {
      sectionX: 0, // Section-relative X
      sectionY: 0,  // Section-relative Y
      isOverSection: false // Track if mouse is over the section
    };

    // Create raycaster for mouse interaction
    this.raycaster = new THREE.Raycaster();
    this.mouseVector = new THREE.Vector2();

    // Listen to input updates from the Input class
    this.input.on('update', this.onInputUpdate);
  }

  /**
   * Handle input updates from the Input class
   * @param {Object} inputState - Current input state
   */
  onInputUpdate(inputState) {
    if(!this.visible) return;

    // Update mouseVector from the normalized input coordinates
    this.mouseVector.set(inputState.normalized.x, inputState.normalized.y);

    // Update raycaster with mouse position
    this.raycaster.setFromCamera(this.mouseVector, this.sceneCamera);

    // Use background plane for intersection
    const intersects = this.raycaster.intersectObject(this.backgroundPlane);

    if (intersects.length > 0) {
      this.mouse.sectionX = intersects[0].point.x;
      this.mouse.sectionY = intersects[0].point.y - this.sectionData.worldData.position.y;
      this.mouse.isOverSection = true;
    } else {
      this.mouse.isOverSection = false;
    }
  }

  createShape(){
    const texture = this.resources.items.subtract;
    const textureBlur = this.resources.items.subtractBlur;

    // Define shape dimensions in pixels
    this.shapeSize = {
      widthPx: 103,
      heightPx: 52,
    };

    // Convert to world units
    this.shapeSize.widthUnits = this.sizes.pixelsToWorldUnits(this.shapeSize.widthPx);
    this.shapeSize.heightUnits = this.sizes.pixelsToWorldUnits(this.shapeSize.heightPx);

    // Create a simple plane geometry at origin with size 1x1
    this.shapeGeometry = new THREE.PlaneGeometry(1, 1);

    // Create material
    this.shapeMaterial = new THREE.ShaderMaterial({
      side: THREE.DoubleSide,
      uniforms: {
        uTexture: { value: texture },
        uTextureBlur: { value: textureBlur },
        uOpacity: { value: null } // Will be set per instance
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
    });

    // Create a group for the instanced mesh
    this.shapesGroup = new THREE.Group();
    this.shapesGroup.name = `${this.constructor.name}-ShapesGroup`;
    this.group.add(this.shapesGroup);
  }

  /**
   * Creates a pattern of shapes that fill the entire section using instanced geometry
   */
  createShapePattern() {
    // Set spacing between shapes in pixels
    const spacingX = 56;
    const spacingY = 50;

    // Convert spacing to world units
    const spacingXUnits = this.sizes.pixelsToWorldUnits(spacingX);
    const spacingYUnits = this.sizes.pixelsToWorldUnits(spacingY);

    // Get section dimensions
    const sectionWidth = this.sectionData.worldData.width;
    const sectionHeight = this.sectionData.worldData.height;

    // Calculate top padding
    const paddingTopPx = 50;
    const paddingTopUnits = this.sizes.pixelsToWorldUnits(paddingTopPx);

    // Calculate the row height (shape height + spacing between rows)
    const rowHeight = this.shapeSize.heightUnits + spacingYUnits;

    // Calculate column width (shape width + spacing between columns)
    const columnWidth = this.shapeSize.widthUnits + spacingXUnits;

    // Calculate how many shapes we need to fill the section
    const numShapesX = Math.ceil(sectionWidth / columnWidth) + 1;

    // Calculate number of rows needed
    const availableHeight = sectionHeight - paddingTopUnits;
    const numShapesY = Math.ceil(availableHeight / rowHeight);

    // Starting position (the first shape is half visible from the left)
    const startX = -sectionWidth / 2 - this.shapeSize.widthUnits / 2;
    const startY = sectionHeight / 2 - paddingTopUnits;

    // Calculate total number of instances
    const instanceCount = numShapesX * numShapesY;

    // Create instanced mesh
    this.instancedMesh = new THREE.InstancedMesh(
      this.shapeGeometry,
      this.shapeMaterial,
      instanceCount
    );
    this.instancedMesh.name = `${this.constructor.name}-InstancedMesh`;
    this.instancedMesh.position.z = this.positionOffset;
    this.shapesGroup.add(this.instancedMesh);

    // Set up matrices for each instance
    this.instancePositions = [];
    let instanceIndex = 0;

    // Create dummy objects to help with matrix calculations
    const dummyObj = new THREE.Object3D();

    // Initialize opacity arrays
    this.targetOpacities = new Array(instanceCount).fill(this.defaultOpacity);
    this.currentOpacities = new Array(instanceCount).fill(this.defaultOpacity);

    // Create instance opacity attribute
    const opacityArray = new Float32Array(instanceCount);
    for (let i = 0; i < instanceCount; i++) {
      opacityArray[i] = this.defaultOpacity;
    }

    const opacityAttribute = new THREE.InstancedBufferAttribute(opacityArray, 1);
    this.instancedMesh.geometry.setAttribute('aOpacity', opacityAttribute);

    for (let y = 0; y < numShapesY; y++) {
      // Calculate row offset (odd rows are shifted by half the spacing)
      const rowOffset = y % 2 === 1 ? columnWidth / 2 : 0;

      for (let x = 0; x < numShapesX; x++) {
        // Calculate position for this instance
        const posX = startX + x * columnWidth + rowOffset;
        const posY = startY - y * rowHeight;

        // Store position for proximity calculations
        this.instancePositions.push({
          x: posX,
          y: posY,
          index: instanceIndex
        });

        // Set position and scale using the dummy object
        dummyObj.position.set(posX, posY, 0);
        dummyObj.scale.set(this.shapeSize.widthUnits, this.shapeSize.heightUnits, 1);
        dummyObj.updateMatrix();

        // Copy matrix to the instance
        this.instancedMesh.setMatrixAt(instanceIndex, dummyObj.matrix);

        instanceIndex++;
      }
    }

    // Update the instance matrix buffer
    this.instancedMesh.instanceMatrix.needsUpdate = true;
  }

  recreateShapePattern(){
    if(!this.isInit) return;

    if(this.instancedMesh){
      this.shapesGroup.remove(this.instancedMesh);
      this.instancedMesh.geometry.dispose();
      this.instancedMesh.material.dispose();
      this.instancedMesh = null;
    }

    this.instancePositions = [];
    this.targetOpacities = [];
    this.currentOpacities = [];

    this.createShapePattern();
  }

  resize(){
    super.resize();

    this.recreateShapePattern();
  }

  /**
   * Set up animation
   */
  setupAnimation() {
    // Listen for tick events from Time singleton
    this.time.on('tick', () => {
      if (this.visible && this.sizes.isDesktop) {
        this.updateAnimation();
      }
    });
  }

  updateAnimation() {
    // Skip if no instancedMesh or not visible
    if (!this.instancedMesh || !this.visible || !this.instancePositions) return;

    // Maximum distance for interaction (in world units)
    const maxDistance = 3;

    // Update opacities based on mouse proximity
    for (let i = 0; i < this.instancePositions.length; i++) {
      const instance = this.instancePositions[i];

      if (!this.mouse.isOverSection) {
        // Reset to default opacity if mouse is not over the section
        this.targetOpacities[instance.index] = this.defaultOpacity;
        continue;
      }

      // Calculate distance to mouse using section-relative coordinates
      const dx = instance.x - this.mouse.sectionX;
      const dy = instance.y - this.mouse.sectionY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Calculate intensity based on distance (closer = more intense)
      let intensity = 0;
      if (distance < maxDistance) {
        // Linear falloff: 1 at distance 0, 0 at maxDistance
        intensity = 1 - (distance / maxDistance);
        // Apply easing function for a more natural falloff
        intensity = intensity * intensity;
      }

      // Set target opacity based on intensity
      this.targetOpacities[instance.index] = this.defaultOpacity +
        (this.highlightOpacity - this.defaultOpacity) * intensity;
    }

    // Update the instance opacity values with lerping
    let needsUpdate = false;
    for (let i = 0; i < this.targetOpacities.length; i++) {
      const targetOpacity = this.targetOpacities[i];
      let currentOpacity = this.currentOpacities[i];

      // Only update if there's a meaningful difference
      if (Math.abs(currentOpacity - targetOpacity) > 0.001) {
        currentOpacity += (targetOpacity - currentOpacity) * this.opacityLerp;
        this.currentOpacities[i] = currentOpacity;

        // Update the opacity attribute
        this.instancedMesh.geometry.attributes.aOpacity.array[i] = currentOpacity;
        needsUpdate = true;
      }
    }

    // Update the attribute buffer if any changes were made
    if (needsUpdate) {
      this.instancedMesh.geometry.attributes.aOpacity.needsUpdate = true;
    }
  }

  /**
   * Dispose section elements
   * Override base method
   */
  disposeElements() {
    // Remove event listener
    this.time.off('tick');


    // Call parent dispose
    super.disposeElements();
  }
}
